/**
 * Environment Configuration
 *
 * Gestione centralizzata delle variabili d'ambiente con validazione.
 * Tutte le variabili devono essere prefissate con VITE_ per essere esposte al client.
 *
 * @example
 * // Utilizzo
 * import { env } from '@/config/env';
 * console.log(env.API_BASE_URL);
 */

/**
 * Schema delle variabili d'ambiente supportate
 */
interface EnvConfig {
  /** URL base delle API */
  API_BASE_URL: string;
  /** Usa dati mock invece delle API reali */
  USE_MOCK_DATA: boolean;
  /** Ambiente corrente */
  MODE: 'development' | 'production' | 'test';
  /** Abilita logging di debug */
  DEBUG: boolean;
  /** Versione dell'applicazione */
  APP_VERSION: string;
}

/**
 * Parsing sicuro di booleani dalle variabili d'ambiente
 */
function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
}

/**
 * Validazione e parsing delle variabili d'ambiente
 */
function loadEnvConfig(): EnvConfig {
  const mode = import.meta.env.MODE as EnvConfig['MODE'];
  const isDev = mode === 'development';

  return {
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api',
    USE_MOCK_DATA: parseBoolean(import.meta.env.VITE_USE_MOCK_DATA, isDev),
    MODE: mode,
    DEBUG: parseBoolean(import.meta.env.VITE_DEBUG, isDev),
    APP_VERSION: import.meta.env.VITE_APP_VERSION || '0.0.1',
  };
}

/**
 * Configurazione environment validata e tipizzata
 */
export const env = loadEnvConfig();

/**
 * Helper per verificare se siamo in development
 */
export const isDev = env.MODE === 'development';

/**
 * Helper per verificare se siamo in production
 */
export const isProd = env.MODE === 'production';

/**
 * Helper per verificare se usiamo mock data
 */
export const useMockData = env.USE_MOCK_DATA;
